import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Child, IndividualChildResultPreview, IndividualChildResultPreviewContract, ListResponseContract, Product, ProductContract, Test } from '@isophi/teachers-shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { ChildService } from '../../../core/services/child.service';
import { TestService } from '../../../core/services/test.service';

export enum ResultType {
  COMPLETED = 'completed',
  UNCOMPLETED = 'uncompleted'
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  public constructor(
    private http: HttpClient,
    private testService: TestService,
    private childService: ChildService,
  ) {}

  public getLatestChildResultsPreview(resultType?: ResultType): Observable<IndividualChildResultPreview[]> {
    const url = `${environment.teacherAPI}/material-data-set/`;
    const params = new HttpParams().set('status', resultType).set('order_by', '-finished_at').set('limit', '5');

    return this.http.get<ListResponseContract<IndividualChildResultPreviewContract>>(url, { params }).pipe(
      mergeMap((response) =>
        combineLatest({
          childResults: of(response.results),
          children: this.childService.getChildrenByUuids(response.results.map((result) => result.child_uuid)),
          tests: this.testService.tests$,
        })
      ),
      map(({ childResults, children, tests }) =>
        childResults
          .map((resultData) =>
            this.deserializeIndividualResultPreviewData(
              children.find((child) => child.uuid === resultData.child_uuid),
              tests.find((test) => test.uuid === resultData.test_uuid),
              resultData
            )
          )
          .filter((result) => result.child && result.test)
      )
    );
  }

  public getOwnedProducts(): Observable<Product[]> {
    const url = `${environment.teacherAPI}/kindergarten-products/owned/`;

    return this.http.get<ProductContract[]>(url).pipe(
      map((response) => response.map((productData) => Product.deserialize(productData)).sort((a, b) => a.order - b.order))
    );
  }

  public getRecommendedProducts(): Observable<Product[]> {
    const url = `${environment.teacherAPI}/kindergarten-products/recommended/`;

    return this.http.get<ProductContract[]>(url).pipe(
      map((response) => response.map((productData) => Product.deserialize(productData)).sort((a, b) => a.order - b.order))
    );
  }

  public getVideoManuals(): Observable<string[]> {
    const url = `${environment.teacherAPI}/kindergarten-products/video-manuals/`;

    return this.http.get<string[]>(url);
  }

  protected deserializeIndividualResultPreviewData(
    child: Child,
    test: Test,
    resultData: IndividualChildResultPreviewContract
  ): IndividualChildResultPreview {
    return IndividualChildResultPreview.deserialize(resultData, test, child);
  }
}
